import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { PercentField } from "@/shared/components/PercentField";
import type {
	WaterAccountAllocation,
	WaterAllocationTarget,
} from "@/shared/models/WaterAccount";
import {
	applyPercentEdit,
	getDrainOrder,
	isAllocationTotalWithinLimit,
} from "@/shared/utils/allocationSplit";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField, Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
	buildPercentByCode,
	type WaterAccountAllocationRow,
} from "../models/WaterAccountAllocationDraft";
import { upsertWaterAccountAllocations } from "../repositories/WaterAllocationsRepository";

type Props = {
	account: WaterAccountAllocationRow | null;
	allocations: WaterAccountAllocation[];
	targets: WaterAllocationTarget[];
	modal: "update" | "";
	onHide: () => void;
};

export const EditAllocationsModal = ({
	account,
	allocations,
	targets,
	modal,
	onHide,
}: Props) => {
	const queryClient = useQueryClient();

	const [percentByCode, setPercentByCode] = useState<
		Record<string, number | null>
	>({});
	const [percentOrder, setPercentOrder] = useState<string[]>([]);
	const [error, setError] = useState("");

	const targetCodes = targets.map((target) => target.TargetCode);

	const upsertMutation = useMutation(upsertWaterAccountAllocations, {
		meta: { successMessage: "The account split was updated successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["water-accounts"] });
			onHide();
		},
		onError: (error: Error) => setError(error.message),
	});

	const total = targetCodes.reduce(
		(sum, code) => sum + (percentByCode[code] ?? 0),
		0,
	);
	const isTotalValid = isAllocationTotalWithinLimit(total);

	const lastEditedCode = percentOrder[percentOrder.length - 1] ?? null;
	const balancingCode =
		lastEditedCode === null
			? null
			: (getDrainOrder(targetCodes, lastEditedCode, percentOrder)[0] ?? null);

	const onPercentChange = (code: string, value: number | null) => {
		const nextOrder = [...percentOrder.filter((other) => other !== code), code];
		setPercentOrder(nextOrder);
		setPercentByCode((current) =>
			applyPercentEdit(current, targetCodes, code, value, nextOrder),
		);
	};

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (account === null || !isTotalValid) return;

		upsertMutation.mutate({
			WaterAccountId: account.WaterAccountId,
			IsActive: account.IsActive,
			Allocations: targets
				.filter((target) => (percentByCode[target.TargetCode] ?? 0) > 0)
				.map((target) => ({
					TargetId: target.TargetId,
					AllocationPercent: percentByCode[target.TargetCode] as number,
				})),
		});
	};

	useEffect(() => {
		if (modal === "" || account === null) return;

		setPercentByCode(
			buildPercentByCode(targets, allocations, account.WaterAccountId),
		);
		setPercentOrder([]);
	}, [modal, account, targets, allocations]);

	return (
		<>
			{modal === "update" && (
				<HelmetDexteel title="Edit Split - Water Allocations" />
			)}
			<MesfModal
				id="modal-water-allocations"
				title="EDIT SPLIT"
				open={modal !== ""}
				handleClose={onHide}
				maxWidth="sm"
			>
				<form onSubmit={onSubmit}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						<Grid2 container spacing={1}>
							<Grid2 size={{ md: 4, xs: 12 }}>
								<TextField
									label="Account #"
									value={account?.AccountNumber ?? ""}
									disabled
									fullWidth
									size="small"
								/>
							</Grid2>
							<Grid2 size={{ md: 8, xs: 12 }}>
								<TextField
									label="Location"
									value={account?.Location ?? ""}
									disabled
									fullWidth
									size="small"
								/>
							</Grid2>

							{targets.map((target) => {
								const isBalancing = balancingCode === target.TargetCode;

								return (
									<Grid2
										key={target.TargetId}
										size={{ md: 12 / targets.length, xs: 12 }}
									>
										<Tooltip
											title={
												isBalancing
													? "First to give up % when the split goes over 100%"
													: ""
											}
											placement="top"
										>
											<span>
												<PercentField
													label={target.TargetName}
													value={percentByCode[target.TargetCode] ?? null}
													onChange={(value) =>
														onPercentChange(target.TargetCode, value)
													}
													fullWidth
													autoComplete="off"
													size="small"
													margin="none"
													sx={
														isBalancing
															? { "& input": { color: "text.secondary" } }
															: undefined
													}
												/>
											</span>
										</Tooltip>
									</Grid2>
								);
							})}
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={upsertMutation.isPending}
							onHide={onHide}
							buttonLabel="Save"
							buttonColor="primary"
							disabled={!isTotalValid}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
